import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../lib/api";

/**
 * Blog blocks renderer.
 * A block is one of:
 *  - { type: "text",  text: "markdown..." }
 *  - { type: "image", src, caption?, credit?, alt? }
 *  - { type: "ad",    ad_id }
 *  - { type: "video", url, caption? }  // YouTube URL
 */

// Shared markdown components used by both blocks and legacy content
export const mdComponents = {
  h1: (props) => <h2 className="font-display font-bold text-3xl mt-10 mb-4 text-foreground tracking-tight" {...props} />,
  h2: (props) => <h2 className="font-display font-bold text-2xl mt-8 mb-3 text-foreground tracking-tight" {...props} />,
  h3: (props) => <h3 className="font-display font-semibold text-xl mt-6 mb-2 text-primary" {...props} />,
  p: (props) => <p className="text-foreground/85 leading-relaxed mb-4" {...props} />,
  strong: (props) => <strong className="font-semibold text-foreground" {...props} />,
  em: (props) => <em className="italic text-foreground/80" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1.5 text-foreground/85" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-foreground/85" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  a: (props) => <a className="text-primary hover:underline underline-offset-2 font-medium break-words" target="_blank" rel="noreferrer" {...props} />,
  blockquote: (props) => (
    <blockquote className="border-l-4 border-primary/30 bg-primary/5 pl-5 pr-4 py-3 my-6 italic text-foreground/85 rounded-r-lg" {...props} />
  ),
  hr: () => <hr className="my-8 border-primary/10" />,
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="px-1.5 py-0.5 rounded bg-secondary text-primary text-sm" {...props} />
    ) : (
      <code className="block p-4 rounded-lg bg-secondary text-sm overflow-x-auto" {...props} />
    ),
  img: (props) => (
    <img className="w-full rounded-2xl my-6" loading="lazy" {...props} alt={props.alt || ""} />
  ),
};

function youTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const parts = u.pathname.split("/");
    const embedIdx = parts.indexOf("embed");
    if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
    const shortsIdx = parts.indexOf("shorts");
    if (shortsIdx >= 0 && parts[shortsIdx + 1]) return parts[shortsIdx + 1];
  } catch (_) {
    return null;
  }
  return null;
}

function AdBlock({ adId }) {
  const [ad, setAd] = useState(null);
  useEffect(() => {
    if (!adId) return;
    api.get(`/ads/by-id/${adId}`).then((r) => setAd(r.data?.id ? r.data : null)).catch(() => {});
  }, [adId]);
  if (!ad || !ad.image_url) return null;
  return (
    <a
      href={ad.target_url || "#"}
      target={ad.target_url ? "_blank" : undefined}
      rel="noreferrer"
      className="not-prose block my-8 rounded-2xl overflow-hidden bg-secondary border border-primary/10"
      data-testid={`blog-block-ad-${adId}`}
    >
      <img src={ad.image_url} alt={ad.title || "Publicité"} className="w-full h-auto object-cover" />
      <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground bg-white/70">
        Publicité — {ad.title || "Partenaire"}
      </div>
    </a>
  );
}

function VideoBlock({ url, caption }) {
  const vid = youTubeId(url);
  if (!vid) return null;
  return (
    <figure className="my-8" data-testid="blog-block-video">
      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${vid}`}
          title={caption || "Vidéo YouTube"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
      {caption && (
        <figcaption className="text-sm text-muted-foreground text-center mt-2 italic">{caption}</figcaption>
      )}
    </figure>
  );
}

function ImageBlock({ src, caption, credit, alt }) {
  if (!src) return null;
  return (
    <figure className="my-8" data-testid="blog-block-image">
      <img src={src} alt={alt || caption || ""} loading="lazy" className="w-full rounded-2xl" />
      {(caption || credit) && (
        <figcaption className="text-xs text-muted-foreground text-center mt-2">
          {caption}
          {caption && credit && " · "}
          {credit && <span className="italic">Crédit : {credit}</span>}
        </figcaption>
      )}
    </figure>
  );
}

function TextBlock({ text }) {
  return (
    <div data-testid="blog-block-text">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {text || ""}
      </ReactMarkdown>
    </div>
  );
}

export default function BlogBlocks({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <div className="blog-blocks" data-testid="blog-blocks">
      {blocks.map((b, i) => {
        const key = `${b.type}-${i}`;
        switch (b.type) {
          case "text":
            return <TextBlock key={key} text={b.text} />;
          case "image":
            return <ImageBlock key={key} src={b.src} caption={b.caption} credit={b.credit} alt={b.alt} />;
          case "ad":
            return <AdBlock key={key} adId={b.ad_id} />;
          case "video":
            return <VideoBlock key={key} url={b.url} caption={b.caption} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
